import { useQuery } from '@tanstack/react-query';
import {
  SSO_API_BASE,
  SSO_EMAIL_COOKIE,
  SSO_ERROR_CODES,
  SSO_PROVIDERS,
  type SsoErrorCode,
  type SsoPortal,
  type SsoProvider,
  type SsoProviders,
} from '@gokrew/models/sso';

/**
 * Google and Microsoft sign-in, the browser side (spec 2026-10-31 section
 * 6.3). The buttons are plain navigations to the backend's start route; the
 * callback comes back to `/login?sso=complete` (the refresh cookie is set)
 * or `/login?sso_error=<code>`, and from a profile link to the profile with
 * `?sso_linked=<provider>` or `?sso_error=<code>`.
 */

const isProvider = (value: string | null): value is SsoProvider =>
  !!value && (SSO_PROVIDERS as readonly string[]).includes(value);

const isErrorCode = (value: string | null): value is SsoErrorCode =>
  !!value && (SSO_ERROR_CODES as readonly string[]).includes(value);

const hasControlOrBackslash = (value: string) =>
  [...value].some((ch) => {
    const code = ch.charCodeAt(0);
    return ch === '\\' || code < 0x20 || code === 0x7f;
  });

/** Relative portal paths only, the same rule the backend enforces. */
export function isSafeSsoReturnTo(value: string | null | undefined) {
  return (
    !!value &&
    value.length <= 512 &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !hasControlOrBackslash(value)
  );
}

export function ssoStartUrl(
  provider: SsoProvider,
  portal: SsoPortal,
  returnTo?: string | null
): string {
  const params = new URLSearchParams({ portal });
  if (isSafeSsoReturnTo(returnTo)) params.set('returnTo', returnTo!);
  return `${SSO_API_BASE}/${provider}/start?${params.toString()}`;
}

/** Which buttons to show; public, cached for the page's life. */
export function useSsoProviders() {
  return useQuery<SsoProviders>({
    queryKey: ['sso-providers'],
    queryFn: async () => {
      const res = await fetch(`${SSO_API_BASE}/providers`, {
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`sso providers ${res.status}`);
      const body = (await res.json()) as { data: SsoProviders };
      return body.data;
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

export interface SsoRedirectResult {
  /** The callback signed the person in; the portal must refresh. */
  complete: boolean;
  error: SsoErrorCode | null;
  provider: SsoProvider | null;
  /** This sign-in or profile link linked a provider for the first time. */
  linked: SsoProvider | null;
  /** The address of a refused sign-in, read once from its cookie. */
  email: string | null;
}

/** Reads (and deletes) the short-lived cookie the callback set. */
function takeEmailCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const entry = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${SSO_EMAIL_COOKIE}=`));
  if (!entry) return null;
  document.cookie = `${SSO_EMAIL_COOKIE}=; Max-Age=0; path=/; SameSite=Lax`;
  try {
    return decodeURIComponent(entry.slice(SSO_EMAIL_COOKIE.length + 1));
  } catch {
    return null;
  }
}

export function readSsoRedirect(search: string): SsoRedirectResult {
  const params = new URLSearchParams(search);
  const error = params.get('sso_error');
  const provider = params.get('sso_provider');
  const linked = params.get('sso_linked');
  const hasError = isErrorCode(error);
  return {
    complete: params.get('sso') === 'complete',
    error: hasError ? error : null,
    provider: isProvider(provider) ? provider : null,
    linked: isProvider(linked) ? linked : null,
    email: hasError ? takeEmailCookie() : null,
  };
}

/** Drops the SSO markers from the address bar (keeps `redirect`). */
export function clearSsoParams(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of ['sso', 'sso_error', 'sso_provider', 'sso_linked'])
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  if (changed)
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
}
